// FILE: src/pages/AdminFeedbackListPage.jsx
// Course survey (plan WP-8 / WP-6, MTO 10.3): every response with its
// overall rating as words ("4.2 out of 5"), a summary (responses, average
// overall and the average per topic as a table: text values, never colour
// alone) and a link to each response. The filters (overall rating, dates,
// one student) are kept in the URL (?rating=&from=&to=&userId=) and sent to
// the server (GET /api/feedback?rating&startDate&endDate&userId).
// WP-7 (R4): the responses table pages on the server (?page=&limit= →
// { feedback, totalCount, page, limit, totalPages }; the page number is kept
// in the URL); the summary still reads every matching response.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import fetchWithAuth from "../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, StatCard, visuallyHidden } from "../components/ui";
import { FigureGroup, ResponsiveTable } from "../components/admin/AdminBits";
import { ServerPagination } from "../components/admin/AuditLogTable";
import { formatDate, formatNumber, personName } from "../components/admin/format";
import {
  LEGACY_NOTE,
  averageOverall,
  isLegacySurvey,
  overallRating,
  ratingText,
  surveyStudent,
  topicAverages
} from "../components/admin/surveyTopics";

const RATINGS = ["5", "4", "3", "2", "1"];
const PAGE_SIZE = 25;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ID_RE = /^[a-f0-9]{24}$/i;

export default function AdminFeedbackListPage() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ list: null, loading: true, error: null });
  const [table, setTable] = useState({ rows: null, totalCount: 0, totalPages: 1, loading: true, error: null });
  const latest = useRef(0);
  const latestPage = useRef(0);

  const rating = RATINGS.includes(params.get("rating")) ? params.get("rating") : "";
  const from = DATE_RE.test(params.get("from") || "") ? params.get("from") : "";
  const to = DATE_RE.test(params.get("to") || "") ? params.get("to") : "";
  const userId = ID_RE.test(params.get("userId") || "") ? params.get("userId") : "";
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);

  const filterQuery = useCallback(() => {
    const query = new URLSearchParams();
    if (rating) query.set("rating", rating);
    if (from) query.set("startDate", from);
    if (to) query.set("endDate", to);
    if (userId) query.set("userId", userId);
    return query;
  }, [rating, from, to, userId]);

  // Every matching response, for the summary figures.
  const load = useCallback(() => {
    const id = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/feedback?${filterQuery().toString()}`)
      .then((data) => {
        if (id === latest.current) setState({ list: Array.isArray(data) ? data : [], loading: false, error: null });
      })
      .catch((error) => {
        if (id === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [filterQuery]);

  // One page of responses for the table (server paging).
  const loadPage = useCallback(() => {
    const id = ++latestPage.current;
    const query = filterQuery();
    query.set("page", String(page));
    query.set("limit", String(PAGE_SIZE));
    setTable((t) => ({ ...t, loading: true, error: null }));
    fetchWithAuth(`/api/feedback?${query.toString()}`)
      .then((data) => {
        if (id !== latestPage.current) return;
        if (Array.isArray(data)) {
          // A server without paging sends every response: page it here.
          setTable({
            rows: data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
            totalCount: data.length,
            totalPages: Math.max(1, Math.ceil(data.length / PAGE_SIZE)),
            loading: false,
            error: null
          });
        } else {
          setTable({
            rows: Array.isArray(data?.feedback) ? data.feedback : [],
            totalCount: Number(data?.totalCount) || 0,
            totalPages: Number(data?.totalPages) || 1,
            loading: false,
            error: null
          });
        }
      })
      .catch((error) => {
        if (id === latestPage.current) setTable((t) => ({ ...t, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [filterQuery, page]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page"); // a new filter starts on page 1
    setParams(next, { replace: true });
  };

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    if (p > 1) next.set("page", String(p));
    else next.delete("page");
    setParams(next);
  };

  const { list, loading, error } = state;
  const shown = Array.isArray(list) ? list : [];
  const rows = Array.isArray(table.rows) ? table.rows : [];
  const pageTotal = table.rows === null ? shown.length : table.totalCount;
  const filtered = !!(rating || from || to || userId);
  const average = averageOverall(shown);
  const topics = topicAverages(shown);
  const legacyCount = shown.filter(isLegacySurvey).length;
  const oneStudent = userId && shown[0] ? personName(surveyStudent(shown[0]), "this student") : "one student";

  return (
    <Box>
      <PageHeader
        title="Course survey"
        description="What students said about the course when they finished it: a rating out of 5 for five topics, and their written suggestions."
      />

      {list === null && error ? (
        <ErrorState error={error} title="We couldn't load the survey responses" onRetry={load} />
      ) : list === null ? (
        <LoadingState variant="page" label="Loading survey responses…" />
      ) : (
        <>
          <FigureGroup
            id="survey-summary"
            title="Summary"
            description={filtered ? "For the responses that match your filters." : "For every response so far."}
            columns={2}
          >
            <StatCard
              icon={RateReviewOutlinedIcon}
              label="Responses"
              value={formatNumber(shown.length)}
              note={legacyCount ? `${legacyCount} sent before the six-topic survey` : undefined}
            />
            <StatCard
              icon={StarBorderIcon}
              label="Average overall rating"
              value={ratingText(average)}
              note="The average of each response's overall rating."
            />
          </FigureGroup>

          <Section
            id="topics"
            title="Average by topic"
            description="Six-topic responses only. Each topic is rated from 1 (poor) to 5 (very good)."
            sx={{ mb: 3 }}
          >
            <ResponsiveTable label="Average rating by topic" minWidth={480}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Topic
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Average rating
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Ratings
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topics.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      {t.label}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{ratingText(t.average)}</TableCell>
                    <TableCell align="right">{formatNumber(t.count)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          </Section>

          <Section id="responses" title="Responses" contentSx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2, alignItems: { md: "flex-start" } }}>
              <TextField
                select
                id="survey-rating"
                label="Overall rating"
                value={rating}
                onChange={(e) => setParam("rating", e.target.value)}
                sx={{ minWidth: 220 }}
                helperText="Rounded to a whole number"
              >
                <MenuItem value="">All ratings</MenuItem>
                {RATINGS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r} out of 5
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="survey-from"
                label="Sent from"
                type="date"
                value={from}
                onChange={(e) => setParam("from", e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || undefined } }}
              />
              <TextField
                id="survey-to"
                label="Sent to"
                type="date"
                value={to}
                onChange={(e) => setParam("to", e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
              />
              {filtered && (
                <Button
                  startIcon={<FilterAltOffOutlinedIcon />}
                  onClick={() => setParams({}, { replace: true })}
                  sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
                >
                  Clear filters
                </Button>
              )}
            </Stack>
            {userId && <Typography sx={{ mb: 1, fontWeight: 600 }}>Showing the responses from {oneStudent}.</Typography>}
            <Typography role="status" sx={{ color: "text.secondary", mb: 1 }}>
              {filtered
                ? `${pageTotal} ${pageTotal === 1 ? "response matches" : "responses match"}`
                : `${pageTotal} ${pageTotal === 1 ? "response" : "responses"}, newest first`}
            </Typography>
            <Box sx={{ height: 8, mb: 1 }}>
              {(loading || table.loading) && <LinearProgress aria-label="Updating the responses" />}
            </Box>
            {error && <ErrorState compact error={error} onRetry={load} />}
            {table.error && <ErrorState compact error={table.error} onRetry={loadPage} />}

            {rows.length === 0 && table.rows === null ? (
              <LoadingState variant="table" rows={3} label="Loading the responses…" />
            ) : rows.length === 0 ? (
              filtered ? (
                <EmptyState compact title="No responses match" description="Choose another rating or a wider date range." />
              ) : (
                <EmptyState
                  compact
                  icon={RateReviewOutlinedIcon}
                  title="No survey responses yet"
                  description="Responses appear here when students finish the course and send the survey."
                />
              )
            ) : (
              <>
                <ResponsiveTable label="Survey responses" minWidth={760}>
                  <TableHead>
                    <TableRow>
                      <TableCell component="th" scope="col">
                        Student
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Sent
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Overall rating
                      </TableCell>
                      <TableCell component="th" scope="col" align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((f) => {
                      const student = surveyStudent(f);
                      const who = personName(student, "Unknown student");
                      const email = student?.email;
                      const overall = overallRating(f);
                      return (
                        <TableRow key={f._id} hover>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                            {who}
                            {email && email !== who && (
                              <Typography sx={{ color: "text.secondary", fontWeight: 400 }}>{email}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(f.createdAt)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                              {overall !== null && <StarIcon aria-hidden sx={{ color: "warning.main" }} />}
                              <Typography component="span" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                {overall === null ? "Not rated" : ratingText(overall)}
                              </Typography>
                            </Stack>
                            {isLegacySurvey(f) && <Typography sx={{ color: "text.secondary" }}>{LEGACY_NOTE}</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Button component={RouterLink} to={`/admin/feedback/${f._id}`} variant="outlined">
                              View survey
                              <Box component="span" sx={visuallyHidden}>
                                {` from ${who}`}
                              </Box>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ResponsiveTable>
                <ServerPagination
                  totalCount={table.totalCount}
                  page={Math.min(page, Math.max(1, table.totalPages))}
                  limit={PAGE_SIZE}
                  options={[PAGE_SIZE]}
                  onPage={goToPage}
                  onLimit={() => {}}
                />
              </>
            )}
          </Section>
        </>
      )}
    </Box>
  );
}
